/**
 * A labelled single-select over a fixed enum.
 *
 * Lifted out of `manual/RecordManualPaymentForm.tsx` when the queue gained
 * filters and needed the same control — the alternative was a second copy of a
 * styled `<select>`, which is how the source-type labels came to diverge in the
 * first place.
 *
 * Still a native `<select>` rather than a `componentsv2` primitive, and
 * deliberately so: `componentsv2` has no single-select form field. `Select` is a
 * multi-select autocomplete (`Autocomplete<SelectOption, true, true, true>`) and
 * `Dropdown` is a sort-style trigger with no label, error or helper-text
 * affordances. That gap is flagged in two other places in this repo
 * (`consulting-engagements/.../ProjectSection.tsx`, and the form this came
 * from); inventing a primitive here as a side effect of a bug fix would be the
 * wrong place to close it.
 *
 * `min-h-[44px]` is the touch-target floor, not decoration.
 */
export default function EnumField({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <label htmlFor={id} className="text-on-surface-muted util-caption">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[44px] rounded-md border border-surface-border bg-surface-card px-12 py-10 text-on-surface focus:border-brand-primary focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
